import { Badge } from "@/components/ui/badge";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { ExternalLink, Mail, FileText, Webhook, Clock } from "lucide-react";
import { EmailRecord } from "../services/get-emails";

const statusClassMap: Record<EmailRecord["email_status"], string> = {
  QUEUED: "bg-slate-100 text-slate-700 border-slate-200",
  SENT: "bg-blue-100 text-blue-700 border-blue-200",
  DELIVERED: "bg-green-100 text-green-700 border-green-200",
  DELIVERY_DELAYED: "bg-amber-100 text-amber-700 border-amber-200",
  OPENED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  CLICKED: "bg-purple-100 text-purple-700 border-purple-200",
  BOUNCED: "bg-rose-100 text-rose-700 border-rose-200",
  DELIVERY_FAILED: "bg-red-100 text-red-700 border-red-200",
  COMPLAINED: "bg-orange-100 text-orange-700 border-orange-200",
  CANCELED: "bg-zinc-100 text-zinc-700 border-zinc-200",
  UNKNOWN: "bg-gray-100 text-gray-700 border-gray-200",
};

interface EmailCardProps {
  email: EmailRecord;
  onRetry: (emailId: string) => void;
  isRetrying: boolean;
}

export function EmailCard({ email, onRetry, isRetrying }: EmailCardProps) {
  const fallbackTemplateId = "87d8f053-a4d9-46cc-8700-a2927c691bd6";

  const templateLink = `https://resend.com/templates/${
    email.template_id || fallbackTemplateId
  }`;

  const resendEmailLink = `https://resend.com/emails/${encodeURIComponent(
    email.provider_email_id,
  )}`;

  const formattedIST = new Date(email.created_at).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "medium",
    timeStyle: "short",
  });

  const relation = email.invoice_number
    ? {
        label: "Invoice",
        value: email.invoice_number,
        link: `https://ops.rockethealth.app/invoices?search=${encodeURIComponent(
          email.invoice_number,
        )}&page=0`,
      }
    : null;

  const retryableStatuses = [
    "BOUNCED",
    "DELIVERY_FAILED",
    "COMPLAINED",
    "CANCELED",
  ];

  const canRetry =
    Boolean(email.invoice_id) && retryableStatuses.includes(email.email_status);

  return (
    <div className="group rounded-xl border border-gray-200 bg-white p-4 space-y-4 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs text-gray-500">Recipient</p>
          <p className="text-sm font-semibold text-gray-900 break-all">
            {email.recipient_email || "N/A"}
          </p>
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formattedIST} IST
          </p>
        </div>

        <Badge
          variant="outline"
          className={`text-xs font-medium ${
            statusClassMap[email.email_status] || statusClassMap.UNKNOWN
          }`}
        >
          {email.email_status.replaceAll("_", " ")}
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
        <div>
          <p className="text-gray-500">Subject</p>
          <p className="text-gray-900 truncate font-medium">
            {email.subject || "Untitled"}
          </p>
        </div>

        {relation && (
          <div>
            <p className="text-gray-500">{relation.label}</p>
            <a
              href={relation.link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-mono text-[11px]"
            >
              {relation.value}
            </a>
          </div>
        )}

        <div>
          <p className="text-gray-500">Email ID</p>
          <p className="text-gray-900 truncate font-mono text-[11px]">
            {email.id}
          </p>
        </div>
      </div>

      {(email.failure_reason || email.bounce_reason) && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <p className="font-medium mb-0.5">Delivery Issue</p>
          <p>{email.failure_reason || email.bounce_reason}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-4 text-xs">
          <a
            href={templateLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
          >
            <FileText className="h-3 w-3" />
            Template <ExternalLink className="h-3 w-3" />
          </a>

          <a
            href={resendEmailLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
          >
            <Mail className="h-3 w-3" />
            Email <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <HasAccess permission="write">
        <Button
          variant="outline"
          size="sm"
          disabled={!canRetry || isRetrying}
          onClick={() => onRetry(email.id)}
          className="min-w-[110px]"
        >
          {isRetrying ? "Retrying..." : "Retry Email"}
        </Button>
        </HasAccess>
      </div>
    </div>
  );
}
